import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Monitor, LayoutDashboard, Film, Users, DollarSign, Shield, MessageSquare,
  LogOut, ChevronLeft, ChevronRight, Settings, FolderKanban, BarChart3,
  FileText, UserCheck, Wallet, ClipboardCheck, AlertTriangle, Bot,
  Calculator, Layers, Eye, Zap, Trophy, Scale, Package, TrendingUp, Truck, Activity, GitBranch, ShieldAlert, Mail, Upload, CalendarDays, Building2, ListTree,
  AtSign, Hammer, Image as ImageIcon
} from 'lucide-react';

const ROLE_NAV = {
  master_admin: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'New Client', path: '/clients/new', icon: UserCheck },
    { label: 'Client Portfolio', path: '/clients/portfolio', icon: Building2 },
    { label: 'Email Campaigns', path: '/admin/email', icon: Mail },
    { label: 'Admin Tools', path: '/admin/tools', icon: Hammer },
    { label: 'Emergency', path: '/admin/emergency', icon: ShieldAlert },
    { label: 'Projects', path: '/production', icon: Film },
    { label: 'Pipeline', path: '/pipeline', icon: BarChart3 },
    { label: 'Tasks', path: '/tasks', icon: FolderKanban },
    { label: 'Submit for Approval', path: '/submit', icon: Upload },
    { label: 'Approvals', path: '/reviews', icon: Eye },
    { label: 'Supervisor Feed', path: '/supervisor-feed', icon: Activity },
    { label: 'Timeline', path: '/gantt', icon: Activity },
    { label: 'Templates', path: '/templates', icon: Package },
    { label: 'HR', path: '/hr', icon: Users },
    { label: 'Resources', path: '/resources', icon: Zap },
    { label: 'Contracts', path: '/contracts', icon: FileText },
    { label: 'Analytics', path: '/analytics', icon: Trophy },
    { label: 'Accounting', path: '/accounting', icon: DollarSign },
    { label: 'Bid Estimation', path: '/bid-estimation', icon: Calculator },
    { label: 'Allotment', path: '/allotment', icon: GitBranch },
    { label: 'Vawworld Agent', path: '/ai-agent', icon: Bot },
    { label: 'Mission Bots', path: '/bots', icon: ShieldAlert },
    { label: 'Architecture', path: '/architecture', icon: GitBranch },
    { label: 'Maintenance Tree', path: '/maintenance-tree', icon: ListTree },
    { label: 'Notifications', path: '/notifications', icon: Mail },
    { label: 'Project Members', path: '/members', icon: Users },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    { label: 'Disputes', path: '/disputes', icon: Scale },
    { label: 'Vendors', path: '/vendors', icon: Truck },
    { label: 'Rate Card', path: '/rate-card', icon: Calculator },
    { label: 'Audit & Security', path: '/audit', icon: Shield },
    { label: 'Workstation Pulse', path: '/admin/workstation-pulse', icon: Monitor },
    { label: 'KPI Cockpit',      path: '/admin/kpi-cockpit',      icon: TrendingUp },
    { label: 'HR Pulse',         path: '/admin/hr-pulse',         icon: Activity },
    { label: 'My KPIs',          path: '/dashboard/my-kpis',      icon: BarChart3 },
  ],
  production_manager: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'My Projects', path: '/production', icon: Film },
    { label: 'Pipeline', path: '/pipeline', icon: BarChart3 },
    { label: 'Tasks', path: '/tasks', icon: FolderKanban },
    { label: 'Submit for Approval', path: '/submit', icon: Upload },
    { label: 'Approvals', path: '/reviews', icon: Eye },
    // Founder ask Feb 9, 2026: PMs need a chronological view of every
    // supervisor verdict so they don't miss revisions/approvals as
    // artists flip status to REVIEW. Click-through opens the
    // submission in side-by-side player.
    { label: 'Supervisor Feed', path: '/supervisor-feed', icon: Activity },
    { label: 'Timeline', path: '/gantt', icon: Activity },
    { label: 'Allotment', path: '/allotment', icon: GitBranch },
    { label: 'Rate Card', path: '/rate-card', icon: Calculator },
    { label: 'Project Members', path: '/members', icon: Users },
    { label: 'Resources', path: '/resources', icon: Zap },
    { label: 'Templates', path: '/templates', icon: Package },
    { label: 'Notifications', path: '/notifications', icon: Mail },
    { label: 'Profile', path: '/profile', icon: Settings },
  ],
  accountant: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Accounting', path: '/accounting', icon: DollarSign },
    { label: 'Invoices', path: '/invoices', icon: FileText },
    { label: 'Payouts', path: '/payouts', icon: Wallet },
  ],
  // Removed an earlier 3-item `supervisor:` entry that was being
  // overridden anyway — the rich nav below is now canonical.
  hr_manager: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Freelancers', path: '/users', icon: Users },
    { label: 'Contracts', path: '/contracts', icon: FileText },
    { label: 'Resources', path: '/resources', icon: Zap },
    { label: 'Onboarding', path: '/onboarding', icon: UserCheck },
    { label: 'HR Pulse', path: '/admin/hr-pulse', icon: Activity },
    { label: 'KPI Cockpit', path: '/admin/kpi-cockpit', icon: TrendingUp },
    { label: 'My KPIs', path: '/dashboard/my-kpis', icon: BarChart3 },
  ],
  client_producer: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'My Projects', path: '/production', icon: FolderKanban },
    { label: 'Approvals', path: '/reviews', icon: Eye },
    { label: 'Timeline', path: '/gantt', icon: Activity },
    { label: 'Message Center', path: '/messages', icon: MessageSquare },
    { label: 'Profile', path: '/profile', icon: Settings },
  ],
  client_director: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'My Projects', path: '/production', icon: FolderKanban },
    { label: 'Approvals', path: '/reviews', icon: Eye },
    { label: 'Timeline', path: '/gantt', icon: Activity },
    { label: 'Message Center', path: '/messages', icon: MessageSquare },
    { label: 'Profile', path: '/profile', icon: Settings },
  ],
  supervisor: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Submit for Approval', path: '/submit', icon: Upload },
    { label: 'Approvals', path: '/reviews', icon: Eye },
    { label: 'Timeline', path: '/gantt', icon: Activity },
    { label: 'Tasks', path: '/tasks', icon: FolderKanban },
    { label: 'Pipeline', path: '/pipeline', icon: BarChart3 },
  ],
  creative_director: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Submit for Approval', path: '/submit', icon: Upload },
    { label: 'Approvals', path: '/reviews', icon: Eye },
    { label: 'Timeline', path: '/gantt', icon: Activity },
    { label: 'Tasks', path: '/tasks', icon: FolderKanban },
    { label: 'Pipeline', path: '/pipeline', icon: BarChart3 },
  ],
  freelancer: [
    { label: 'Current Status', path: '/dashboard', icon: Activity },
    { label: 'My Allotment', path: '/my-allotment', icon: ClipboardCheck },
    { label: 'My Tasks', path: '/fl-tasks', icon: FolderKanban },
    // Founder spec Feb 2026 — artists need a dedicated read-only inbox for
    // supervisor + AI-Supervisor notes on their own submissions. Replaces
    // the tiny "Producer Notes" chip on each MY TASKS row.
    { label: 'My Approvals', path: '/my-approvals', icon: Eye },
    { label: 'Tracker', path: '/fl-tracker', icon: BarChart3 },
    { label: 'Earnings', path: '/fl-earnings', icon: Wallet },
    // "Submit for Approval" removed May 24, 2026 (founder spec):
    // artists / freelancers / vendors / employees all submit ONLY via
    // the inline SUBMIT button sitting on each task row in MY TASKS.
    // No separate top-level submit screen.
    // "Approvals" REMOVED Feb 2026 (founder spec): artists are no longer
    // allowed into the review tool — they cannot annotate, approve,
    // revise, or add notes. They read producer feedback ONLY from the
    // "Producer Notes" chip on each MY TASKS row. PM relays client
    // feedback; artists never see client comments directly.
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    // Founder ask May 16, 2026: every party — including freelancers —
    // gets the new Slack-style Message Center V3.
    { label: 'Message Center', path: '/messages', icon: MessageSquare },
    { label: 'Raise Ticket', path: '/fl-communicate', icon: AlertTriangle },
    { label: 'My KPIs', path: '/dashboard/my-kpis', icon: BarChart3 },
    { label: 'Profile', path: '/profile', icon: Settings },
  ],
  vendor_producer: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Message Center', path: '/messages', icon: MessageSquare },
    { label: 'My Work Orders', path: '/vendor-work-orders', icon: FileText },
    { label: 'My Tasks', path: '/vendor-tasks', icon: FolderKanban },
    { label: 'My Allotment', path: '/my-allotment', icon: ClipboardCheck },
    { label: 'My Approvals', path: '/my-approvals', icon: Eye },
    { label: 'My Team', path: '/vendor-team', icon: Users },
    // "Approvals" REMOVED Feb 2026 (founder spec): vendor producer
    // also reads feedback only via the MY TASKS row's "Producer Notes"
    // chip. Their L2/L3 artists do the same.
    { label: 'Timeline', path: '/gantt', icon: Activity },
    { label: 'Finance', path: '/vendor-finance', icon: Wallet },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    // Vawworld Agent is for production-side management (PM/admin/HoP) —
    // vendor producers are external and shouldn't see internal AI tools.
    // Founder ask Feb 14, 2026.
    { label: 'Raise Ticket', path: '/vendor-ticket', icon: AlertTriangle },
    { label: 'Profile', path: '/profile', icon: Settings },
  ],
  vendor_artist: [
    // Founder spec (May 13, 2026 PDF): vendor team artists see ONLY
    // these 5 entries. No "My Work Orders" / no Finance / no Earnings.
    // "Approvals" REMOVED Feb 2026: same artist gating as freelancer.
    { label: 'My Profile', path: '/profile', icon: Settings },
    { label: 'My Tasks', path: '/fl-tasks', icon: FolderKanban },
    { label: 'My Allotment', path: '/my-allotment', icon: ClipboardCheck },
    { label: 'My Approvals', path: '/my-approvals', icon: Eye },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    { label: 'Communication', path: '/messages', icon: MessageSquare },
  ],
  vendor_team_manager: [
    // Founder spec May 16, 2026 (Vendor Manager PDF): L2 vendor-side
    // manager between Vendor Producer and team artists. They can see
    // their own tasks + the team they invited, but NEVER see the
    // parent producer's financials or work orders. Communication is
    // a private 1-on-1 thread with the parent producer.
    // "Submit for Approval" removed May 24, 2026 — inline submit only.
    // "Approvals" REMOVED Feb 2026: L2 managers no longer enter the
    // review tool. They read producer feedback via the MY TASKS row.
    { label: 'My Profile', path: '/profile', icon: Settings },
    { label: 'My Tasks', path: '/fl-tasks', icon: FolderKanban },
    { label: 'My Allotment', path: '/my-allotment', icon: ClipboardCheck },
    { label: 'My Approvals', path: '/my-approvals', icon: Eye },
    { label: 'My Team', path: '/vendor-team', icon: Users },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    { label: 'Communication', path: '/messages', icon: MessageSquare },
  ],
};

// ── Creative-leadership template ──────────────────────────────────
// Founder ask May 15, 2026: studio_director, director, art_director,
// animation_director, vfx_supervisor, cg_supervisor, technical_director,
// executive_producer, line_producer — even when filled by freelancers —
// are senior veterans who need the same dashboard as supervisor /
// creative_director (Approvals + Gantt + Tasks + Pipeline + Submit).
// One canonical list, aliased to every leadership role below so
// changes in one place propagate everywhere.
ROLE_NAV.studio_director     = ROLE_NAV.creative_director;
ROLE_NAV.director            = ROLE_NAV.creative_director;
ROLE_NAV.art_director        = ROLE_NAV.creative_director;
ROLE_NAV.animation_director  = ROLE_NAV.creative_director;
ROLE_NAV.vfx_supervisor      = ROLE_NAV.creative_director;
ROLE_NAV.cg_supervisor       = ROLE_NAV.creative_director;
ROLE_NAV.technical_director  = ROLE_NAV.creative_director;
ROLE_NAV.executive_producer  = ROLE_NAV.creative_director;
ROLE_NAV.line_producer       = ROLE_NAV.creative_director;

// Nav by permission level (for 50+ roles)
const LEVEL_NAV = {
  executive: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Projects', path: '/production', icon: Film },
    { label: 'Pipeline', path: '/pipeline', icon: BarChart3 },
    { label: 'Tasks', path: '/tasks', icon: FolderKanban },
    { label: 'HR', path: '/hr', icon: Users },
    { label: 'Analytics', path: '/analytics', icon: Trophy },
    { label: 'Accounting', path: '/accounting', icon: DollarSign },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    { label: 'Vawworld Agent', path: '/ai-agent', icon: Bot },
    { label: 'Audit & Security', path: '/audit', icon: Shield },
  ],
  leadership: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Projects', path: '/production', icon: Film },
    { label: 'Pipeline', path: '/pipeline', icon: BarChart3 },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    { label: 'Vawworld Agent', path: '/ai-agent', icon: Bot },
  ],
  management: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Projects', path: '/production', icon: Film },
    { label: 'Tasks', path: '/tasks', icon: FolderKanban },
    { label: 'Resources', path: '/resources', icon: Zap },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    { label: 'Vawworld Agent', path: '/ai-agent', icon: Bot },
  ],
  employee: [
    { label: 'Current Status', path: '/dashboard', icon: Activity },
    { label: 'My Allotment', path: '/my-allotment', icon: ClipboardCheck },
    { label: 'My Tasks', path: '/em-tasks', icon: FolderKanban },
    // Founder spec Feb 2026 — internal-employee artists also need the
    // dedicated read-only inbox for supervisor + AI-Supervisor notes
    // on their own submissions.
    { label: 'My Approvals', path: '/my-approvals', icon: Eye },
    { label: 'References', path: '/em-references', icon: ImageIcon },
    { label: 'Salary', path: '/em-salary', icon: Wallet },
    // "Submit for Approval" removed May 24, 2026 (founder spec):
    // employees submit ONLY via the inline SUBMIT button on each task
    // row in MY TASKS.
    // "Approvals" REMOVED Feb 2026 (founder spec): internal-employee
    // artists are no longer allowed into the review tool. They read
    // producer feedback ONLY via the "Producer Notes" chip on each
    // MY TASKS row. PM relays client feedback; artists never see
    // client comments directly. This fixes the regression where
    // Khojendra (3D Modeler) saw full Approve/Revise/Annotate on
    // every team submission.
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    // Founder ask May 16, 2026: internal employees move to the new
    // Slack-style Message Center V3. Legacy /em-communicate stays as
    // the in-app "Raise Ticket" surface (single-line message + ticket).
    { label: 'Message Center', path: '/messages', icon: MessageSquare },
    { label: 'Raise Ticket', path: '/em-communicate', icon: AlertTriangle },
    { label: 'Profile', path: '/profile', icon: Settings },
  ],
  artist: [
    { label: 'My Tasks', path: '/dashboard', icon: FolderKanban },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    { label: 'Earnings', path: '/earnings', icon: Wallet },
    { label: 'AI Assistant', path: '/ai-agent', icon: Bot },
    { label: 'Profile', path: '/profile', icon: Settings },
  ],
  external: [
    { label: 'My Tasks', path: '/dashboard', icon: FolderKanban },
    { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText },
    { label: 'Earnings', path: '/earnings', icon: Wallet },
    { label: 'AI Assistant', path: '/ai-agent', icon: Bot },
    { label: 'Profile', path: '/profile', icon: Settings },
  ],
};

// Role colors and labels loaded dynamically from API

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [roles, setRoles] = useState({});
  // ── Approval-inbox pending count (drives blinking red badge) ──────────
  const [pending, setPending] = useState({ mine: 0, supervisor: 0, pm: 0, client: 0, revise_cascade: 0 });
  // ── Global @mentions unread count (drives sidebar 'Mentions' badge) ───
  const [mentionsUnread, setMentionsUnread] = useState(0);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/roles`)
      .then(r => r.json()).then(setRoles).catch(() => {});
  }, []);

  // Perf rewrite (May 24, 2026): was polling /api/reviews/pending-count
  // every 15s for every logged-in user. With 20+ concurrent users that
  // was ~80 hits/min on a 5-query endpoint. Now: 60s base interval +
  // skip when tab is hidden (Page Visibility API) — drops ~85% of load
  // without any visible UX difference (badge counts don't need sub-minute
  // freshness; arrival of new pending items is also pushed via
  // /messaging/unread-summary on a separate, sound-driven path).
  React.useEffect(() => {
    if (!user) return;
    let active = true;
    const fetchCount = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/pending-count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('vos_token') || ''}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (active) setPending(d);
      } catch { /* noop */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchCount(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      active = false; clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user?.user_id]);

  // Live Message Center badge — polls every 30s for total unread across
  // all visible channels (was 8s — perf rewrite May 24, 2026 to relieve
  // the studio-wide lag artists reported when 20+ users were online).
  // Backend now uses a single aggregation pipeline (was N+1 count_documents
  // — see _unread_counts in messaging_v3.py).
  React.useEffect(() => {
    if (!user) return;
    let active = true;
    const fetchMC = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messaging/unread-summary`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('vos_token') || ''}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (active) setMentionsUnread(d.total_unread || 0);
      } catch { /* noop */ }
    };
    fetchMC();
    const iv = setInterval(fetchMC, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchMC(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      active = false; clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user?.user_id]);

  if (!user) return null;
  const roleData = roles[user.role] || {};
  const roleLevel = roleData.level || 'artist';
  // Internal employees (workforce_type === 'employee') with non-special roles get the
  // dedicated employee nav (Tasks · Leaves · Salary · Communicate). Special roles
  // (master_admin, hr_manager, etc.) use their own ROLE_NAV.
  const SPECIAL = ['master_admin', 'production_manager', 'accountant', 'supervisor',
    'hr_manager', 'creative_director', 'vendor_producer', 'vendor_artist',
    'vendor_team_manager',  // L2 vendor mgr (founder ask May 16, 2026)
    'client_producer', 'client_director', 'freelancer',
    // Creative leadership roles share creative_director's sidebar.
    // Founder ask May 15, 2026.
    'studio_director', 'director', 'art_director', 'animation_director',
    'vfx_supervisor', 'cg_supervisor', 'technical_director',
    'executive_producer', 'line_producer'];
  const isInternalEmployee = user.workforce_type === 'employee' && !SPECIAL.includes(user.role);
  const navItems = ROLE_NAV[user.role]
    || (isInternalEmployee ? LEVEL_NAV.employee : (LEVEL_NAV[roleLevel] || LEVEL_NAV.artist));
  // Inject universal "Mentions" + "Message Center" + "Nomenclature"
  // entries for every role — all three must be reachable from every
  // sidebar (admin/PM/freelancer/client/supervisor/director). Plain
  // function (not useMemo) because we're past an early-return and
  // hooks must always run in the same order.
  //
  // Founder ask Feb 11 2026: the Nomenclature Engine is the UNIVERSAL
  // studio naming reference — every member of the ecosystem needs to
  // see it. The PM sidebar was missing the link (it was the ONLY role
  // bucket that hadn't explicitly added it). Rather than patch 7
  // buckets, we inject it once here — automatically covers every
  // current AND future role.
  const navItemsWithMentions = (() => {
    const items = [...navItems];
    const dashIdx = items.findIndex(i => i.path === '/dashboard');
    let insertAt = dashIdx >= 0 ? dashIdx + 1 : 0;
    // Founder ask May 16, 2026: every party gets the new Slack-style
    // Message Center V3 at /messages. The legacy /fl-communicate is now
    // re-labeled as "Raise Ticket" in the freelancer menu, so the
    // exception below is no longer needed — just check for /messages.
    if (!items.some(i => i.path === '/messages')) {
      items.splice(insertAt, 0, { label: 'Message Center', path: '/messages', icon: MessageSquare });
    }
    if (!items.some(i => i.path === '/admin/nomenclature-engine')) {
      // Slot it RIGHT after Dashboard / Message Center so it's
      // immediately discoverable, regardless of which role bucket
      // is in use.
      const after = items.findIndex(i => i.path === '/messages');
      items.splice((after >= 0 ? after + 1 : insertAt), 0,
        { label: 'Nomenclature', path: '/admin/nomenclature-engine', icon: FileText });
    }
    return items;
  })();
  const roleColor = roleData.color || '#3b82f6';
  const roleLabel = roleData.label || user.role;

  const badgeFor = (item) => {
    if (item.path === '/reviews' && pending.mine > 0) return pending.mine;
    if (item.path === '/messages' && mentionsUnread > 0) return mentionsUnread;
    return null;
  };

  return (
    <aside
      data-testid="sidebar"
      className={`h-screen flex flex-col bg-[#0f0f11] border-r border-zinc-800/50 transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'} flex-shrink-0`}
      style={{ borderTopColor: roleColor, borderTopWidth: '2px' }}
    >
      {/* Brand */}
      <div className="flex items-center gap-2.5 px-4 h-14 border-b border-zinc-800/50">
        <div className="w-7 h-7 bg-blue-600 rounded-sm flex items-center justify-center flex-shrink-0">
          <Monitor className="w-4 h-4 text-white" />
        </div>
        {!collapsed && <span className="font-heading font-bold text-sm tracking-tight text-white">VawworldOS</span>}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto text-zinc-600 hover:text-zinc-300 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {navItemsWithMentions.map(item => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          const count = badgeFor(item);
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => navigate(item.path)}
              className={`relative w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-xs font-medium transition-all ${
                isActive
                  ? 'bg-zinc-800 text-white'
                  : 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/50'
              }`}
            >
              <span className="relative flex-shrink-0">
                <Icon className="w-4 h-4" style={isActive ? { color: roleColor } : {}} />
                {count != null && collapsed && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center animate-pulse shadow-[0_0_8px_rgba(244,63,94,0.8)]" data-testid="approvals-badge-dot">{count}</span>
                )}
              </span>
              {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
              {!collapsed && count != null && (
                <span
                  data-testid="approvals-badge"
                  className="ml-auto min-w-[22px] h-[20px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center animate-[pulse_1s_ease-in-out_infinite] shadow-[0_0_10px_rgba(244,63,94,0.9)] ring-1 ring-rose-300/50"
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User Info + Logout */}
      <div className="border-t border-zinc-800/50 px-3 py-3">
        {!collapsed && (
          <div className="mb-2">
            <p className="text-xs font-medium text-zinc-200 truncate">{user.full_name}</p>
            <p className="text-[10px] font-mono text-zinc-600 truncate">{user.email}</p>
            <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium" style={{ backgroundColor: `${roleColor}20`, color: roleColor, border: `1px solid ${roleColor}40` }}>
              {roleLabel}
            </span>
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={logout}
          className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-zinc-500 hover:text-red-400 hover:bg-zinc-800/50 rounded-sm transition-all"
        >
          <LogOut className="w-3.5 h-3.5" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
